'use client'

import { useTranslations } from 'next-intl'
import Link from 'next/link'

import { Button, DropdownMenu } from '@radix-ui/themes'

import { useTableContext, useTableStore } from '../context/table-context'
import { TableActionsDropdownMenuContent } from '../styled'
import { TableGlobalAction } from '../types'
import { useActionErrorHandler } from './table-utils'

const TableGlobalActions = <T,>() => {
    const t = useTranslations('table')

    const { store, id, globalActions } = useTableContext<T>()
    const data = useTableStore<T, 'data'>('data')
    const refreshData = useTableStore<T, 'refreshData'>('refreshData')
    const handleError = useActionErrorHandler()

    if (!globalActions || globalActions.length == 0)
        return null

    const runAction = async (action: TableGlobalAction<T>) => {
        try {
            if (action.action) {
                const { data, selectedRows } = store.get()
                await action.action({
                    data,
                    selectedData: data.filter(item => selectedRows.has(item[id] as string | number)),
                })
            }

            if (action.refresh)
                await refreshData()
        }
        catch (error) {
            handleError(error)
        }
    }

    // the item itself is the control (no nested button / link), so the keyboard works as well
    const renderActionItem = (action: TableGlobalAction<T>, index: number) =>
        action.href ? (
            <DropdownMenu.Item key={index} color={action.color} onSelect={() => runAction(action)} asChild>
                <Link href={action.href}>{action.label}</Link>
            </DropdownMenu.Item>
        ) : (
            <DropdownMenu.Item key={index} color={action.color} onSelect={() => runAction(action)}>
                {action.label}
            </DropdownMenu.Item>
        )

    const filteredActions = globalActions.filter(action =>
        typeof action.disabled === 'function' ? !action.disabled(data) : !action.disabled
    )
    if (filteredActions.length == 0)
        return null

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger>
                <Button>{t('global-actions-button')}</Button>
            </DropdownMenu.Trigger>
            <TableActionsDropdownMenuContent>
                {filteredActions.map(renderActionItem)}
            </TableActionsDropdownMenuContent>
        </DropdownMenu.Root>
    )
}

export default TableGlobalActions
